<template>
  <div class="alerts-banner" :class="bannerClass">
    <template v-if="isEmpty">
      <div class="alerts-banner__empty">
        <span class="mdi mdi-check-circle alerts-banner__empty-icon"></span>
        <span class="alerts-banner__empty-text">
          Todos los contratos en orden · Sin situaciones que requieran atención
        </span>
      </div>
    </template>

    <template v-else>
      <div class="alerts-banner__header" :class="{ 'alerts-banner__header--pulse': pulsing }">
        <div class="alerts-banner__header-left">
          <span class="mdi alerts-banner__header-icon" :class="headerIconClass"></span>
          <span class="alerts-banner__header-label">{{ headerLabel }}</span>
        </div>
        <div class="alerts-banner__header-center">
          <span class="alerts-banner__summary-item">
            <span class="alerts-banner__summary-dot alerts-banner__summary-dot--critical"></span>
            {{ severityCounts.CRITICAL }} crítica{{ severityCounts.CRITICAL === 1 ? '' : 's' }}
          </span>
          <span class="alerts-banner__summary-item">
            <span class="alerts-banner__summary-dot alerts-banner__summary-dot--warning"></span>
            {{ severityCounts.WARNING }} advertencia{{ severityCounts.WARNING === 1 ? '' : 's' }}
          </span>
          <span class="alerts-banner__summary-item">
            <span class="alerts-banner__summary-dot alerts-banner__summary-dot--info"></span>
            {{ severityCounts.INFO }} informativa{{ severityCounts.INFO === 1 ? '' : 's' }}
          </span>
        </div>
        <div class="alerts-banner__header-right">
          <button class="alerts-banner__collapse-btn" @click="toggleCollapsed">
            <span
              class="mdi mdi-chevron-down alerts-banner__collapse-icon"
              :class="{ 'alerts-banner__collapse-icon--collapsed': collapsed }"
            ></span>
          </button>
        </div>
      </div>

<div class="alerts-banner__body-wrapper" :class="{ 'alerts-banner__body-wrapper--expanded': !collapsed }">
  <div class="alerts-banner__body-inner">
    <div class="alerts-banner__body">
      <TransitionGroup name="alert-row" tag="div">
        <div v-for="alert in visibleAlerts" :key="alert.key" class="alerts-banner__row">
          <span class="alerts-banner__row-dot" :class="`alerts-banner__row-dot--${alert.severity.toLowerCase()}`"></span>

          <span
            class="mdi alerts-banner__row-type-icon"
            :class="[typeIcon(alert), `alerts-banner__row-type-icon--${alert.severity.toLowerCase()}`]"
          ></span>

          <span class="alerts-banner__row-text">
            <span class="alerts-banner__row-type" :class="`alerts-banner__row-type--${alert.severity.toLowerCase()}`">
              {{ typeLabel(alert) }}
            </span>
            <span class="alerts-banner__row-sep">·</span>
            <span class="alerts-banner__row-detail">{{ rowContext(alert) }}</span>
            <span class="alerts-banner__row-sep">·</span>
            <span class="alerts-banner__row-time">{{ relativeTime(alert.since) }}</span>
          </span>

          <button class="alerts-banner__row-action" @click="$emit('action', alert)">
            {{ alert.actionLabel }}
          </button>

          <button class="alerts-banner__row-dismiss" @click="$emit('dismiss', alert.key)">
            <span class="mdi mdi-close"></span>
          </button>
        </div>
      </TransitionGroup>

      <button v-if="overflowCount > 0 && !showAll" class="alerts-banner__more" @click="showAll = true">
        Ver {{ overflowCount }} alerta{{ overflowCount === 1 ? '' : 's' }} adicional{{ overflowCount === 1 ? '' : 'es' }}
      </button>
    </div>
  </div>
</div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'

const props = defineProps({
  alerts: { type: Array, default: () => [] },
})

defineEmits(['action', 'dismiss', 'dismiss-all'])

const STORAGE_COLLAPSED_KEY = 'contractsAlertsBanner:collapsed'
const STORAGE_SEEN_CRITICAL_KEY = 'contractsAlertsBanner:seenCritical'

const collapsed = ref(sessionStorage.getItem(STORAGE_COLLAPSED_KEY) !== 'false')
const showAll = ref(false)
const pulsing = ref(false)

function loadSeenCritical() {
  try {
    return new Set(JSON.parse(sessionStorage.getItem(STORAGE_SEEN_CRITICAL_KEY) || '[]'))
  } catch {
    return new Set()
  }
}

let seenCritical = loadSeenCritical()

watch(
  () => props.alerts,
  (newAlerts) => {
    const currentCriticalKeys = newAlerts.filter((a) => a.severity === 'CRITICAL').map((a) => a.key)
    const hasNewCritical = currentCriticalKeys.some((k) => !seenCritical.has(k))

    if (hasNewCritical && collapsed.value) {
      collapsed.value = false
      sessionStorage.setItem(STORAGE_COLLAPSED_KEY, 'false')
      pulsing.value = true
      setTimeout(() => {
        pulsing.value = false
      }, 1000)
    }

    seenCritical = new Set(currentCriticalKeys)
    sessionStorage.setItem(STORAGE_SEEN_CRITICAL_KEY, JSON.stringify([...seenCritical]))
  },
  { immediate: true, deep: true }
)

function toggleCollapsed() {
  collapsed.value = !collapsed.value
  sessionStorage.setItem(STORAGE_COLLAPSED_KEY, String(collapsed.value))
}

const isEmpty = computed(() => props.alerts.length === 0)

const maxSeverity = computed(() => {
  if (props.alerts.some((a) => a.severity === 'CRITICAL')) return 'CRITICAL'
  if (props.alerts.some((a) => a.severity === 'WARNING')) return 'WARNING'
  return 'INFO'
})

const bannerClass = computed(() => {
  if (isEmpty.value) return 'alerts-banner--empty'
  return `alerts-banner--${maxSeverity.value.toLowerCase()}`
})

const headerIconClass = computed(() => {
  if (maxSeverity.value === 'CRITICAL') return 'mdi-bell-alert'
  if (maxSeverity.value === 'WARNING') return 'mdi-bell-alert-outline'
  return 'mdi-bell-outline'
})

const severityCounts = computed(() => ({
  CRITICAL: props.alerts.filter((a) => a.severity === 'CRITICAL').length,
  WARNING: props.alerts.filter((a) => a.severity === 'WARNING').length,
  INFO: props.alerts.filter((a) => a.severity === 'INFO').length,
}))

const headerLabel = computed(() => {
  const total = props.alerts.length
  const types = new Set(props.alerts.map((a) => a.type))

  if (types.size === 1) {
    const [type] = types
    if (type === 'contract-expired') return `${total} contrato${total === 1 ? '' : 's'} requiere${total === 1 ? 'n' : 'n'} atención inmediata`
    if (type === 'contract-expiring') return `${total} vencimiento${total === 1 ? '' : 's'} próximo${total === 1 ? '' : 's'} esta semana`
    if (type === 'invoice-overdue') return `${total} pago${total === 1 ? '' : 's'} vencido${total === 1 ? '' : 's'}`
    if (type === 'contract-escalation') return `${total} escalación${total === 1 ? '' : 'es'} pendiente${total === 1 ? '' : 's'}`
  }

  return `${total} alerta${total === 1 ? '' : 's'} de contratos`
})

const visibleAlerts = computed(() => {
  return showAll.value ? props.alerts : props.alerts.slice(0, 4)
})

const overflowCount = computed(() => Math.max(0, props.alerts.length - 4))

const TYPE_META = {
  'contract-expired': { icon: 'mdi-file-remove-outline', label: 'Contrato vencido' },
  'contract-expiring': { icon: 'mdi-file-clock-outline', label: 'Por vencer' },
  'contract-escalation': { icon: 'mdi-trending-up', label: 'Escalación pendiente' },
  'invoice-overdue': { icon: 'mdi-cash-remove', label: null },
  'broken-commitment': { icon: 'mdi-calendar-remove-outline', label: 'Compromiso incumplido' },
}

function typeIcon(alert) {
  return TYPE_META[alert.type]?.icon || 'mdi-alert-outline'
}

function typeLabel(alert) {
  if (alert.type === 'invoice-overdue') {
    return alert.severity === 'CRITICAL' ? 'Cartera crítica' : 'Pago pendiente'
  }
  return TYPE_META[alert.type]?.label || 'Alerta'
}

function rowContext(alert) {
  const parts = [alert.tenantName]
  if (alert.plotName) parts.push(alert.plotName)
  return `${parts.join(' — ')} · ${alert.detail}`
}

function relativeTime(dateStr) {
  const date = new Date(dateStr)
  const diffMs = Date.now() - date.getTime()
  const hours = Math.floor(diffMs / (60 * 60 * 1000))
  if (hours < 1) return 'Hace unos min'
  if (hours < 24) return `Hace ${hours}h`
  const days = Math.floor(hours / 24)
  return `Hace ${days}d`
}
</script>

<style scoped>
.alerts-banner {
  margin: 0 1.5rem 1.5rem;
  border: 1px solid var(--border-active);
  border-left: 4px solid transparent;
  border-radius: 10px;
  background: var(--bg-surface-1);
  overflow: hidden;
  transition: border-color 150ms ease;
  container-type: inline-size;
  container-name: alerts-banner;
}
.alerts-banner--critical {
  border-left-color: var(--accent-red);
}

.alerts-banner--warning {
  border-left-color: var(--accent-amber);
}

.alerts-banner--info {
  border-left-color: var(--accent-blue);
}

.alerts-banner--empty {
  border-left-color: var(--accent-green);
}

/* Animación de expandir/contraer */
.alerts-banner__body-wrapper {
  max-height: 0;
  overflow: hidden;
  transition: max-height 200ms ease;
}

.alerts-banner__body-wrapper--expanded {
  max-height: 280px;
}

.alerts-banner__body-inner {
  overflow-y: auto;
  max-height: 280px;
}
.alerts-banner__body-inner::-webkit-scrollbar {
  width: 6px;
}

.alerts-banner__body-inner::-webkit-scrollbar-thumb {
  background: var(--border-active);
  border-radius: 3px;
}

.alerts-banner__body-inner::-webkit-scrollbar-track {
  background: transparent;
}

/* Estado vacío */
.alerts-banner__empty {
  height: 36px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0 1.25rem;
}

.alerts-banner__empty-icon {
  color: var(--alert-empty-text);
  font-size: 1rem;
}

.alerts-banner__empty-text {
  font-family: var(--font-body);
  font-size: 13px;
  color: var(--alert-empty-text);
}

/* Encabezado */
.alerts-banner__header {
  height: 44px;
  display: flex;
  align-items: center;
  padding: 0 1.25rem;
  gap: 1rem;
}

.alerts-banner__header--pulse {
  animation: alert-pulse 1s ease-in-out;
}

@keyframes alert-pulse {
  0%, 100% { background: transparent; }
  50% { background: var(--alert-critical-border); }
}

.alerts-banner__header-left {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  min-width: 0;
  flex-shrink: 1;
}

.alerts-banner--critical .alerts-banner__header-icon,
.alerts-banner--critical .alerts-banner__header-label {
  color: var(--accent-red);
}

.alerts-banner--warning .alerts-banner__header-icon,
.alerts-banner--warning .alerts-banner__header-label {
  color: var(--accent-amber);
}

.alerts-banner--info .alerts-banner__header-icon,
.alerts-banner--info .alerts-banner__header-label {
  color: var(--accent-blue);
}



@container alerts-banner (max-width: 560px) {
  .alerts-banner__header-center {
    display: none;
  }
}

.alerts-banner__header-icon {
  font-size: 16px;
  flex-shrink: 0;
}

.alerts-banner__header-label {
  font-family: var(--font-body);
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.alerts-banner__header-center {
  display: flex;
  align-items: center;
  gap: 1rem;
  font-family: var(--font-body);
  font-size: 12px;
  color: var(--text-content);
  flex-shrink: 0;
}

.alerts-banner__pill-dot {
  color: var(--text-muted);
}

.alerts-banner__summary-item {
  display: flex;
  align-items: center;
  gap: 0.35rem;
}

.alerts-banner__summary-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  flex-shrink: 0;
}

.alerts-banner__summary-dot--critical { background: var(--accent-red); }
.alerts-banner__summary-dot--warning { background: var(--accent-amber); }
.alerts-banner__summary-dot--info { background: var(--accent-blue); }

.alerts-banner__header-right {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
  justify-content: flex-end;
}

.alerts-banner__collapse-btn {
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  padding: 0;
}

.alerts-banner__collapse-icon {
  font-size: 18px;
  color: var(--text-muted);
  transition: transform 150ms ease;
}

.alerts-banner__collapse-icon--collapsed {
  transform: rotate(180deg);
}

/* Filas de alerta */
.alerts-banner__body {
  padding-bottom: 0.25rem;
}

.alerts-banner__row {
  height: 40px;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0 1.25rem;
  border-top: 1px solid inherit;
}

.alert-row-move,
.alert-row-enter-active,
.alert-row-leave-active {
  transition: all 200ms ease;
}

.alert-row-enter-from,
.alert-row-leave-to {
  opacity: 0;
}

.alert-row-leave-active {
  position: absolute;
}

.alerts-banner__row-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.alerts-banner__row-dot--critical { background: var(--accent-red); }
.alerts-banner__row-dot--warning { background: var(--accent-amber); }
.alerts-banner__row-dot--info { background: var(--accent-blue); }

.alerts-banner__row-type-icon {
  font-size: 14px;
  flex-shrink: 0;
}

.alerts-banner__row-type-icon--critical { color: var(--accent-red); opacity: 0.7; }
.alerts-banner__row-type-icon--warning { color: var(--accent-amber); opacity: 0.7; }
.alerts-banner__row-type-icon--info { color: var(--accent-blue); opacity: 0.7; }

.alerts-banner__row-text {
  flex: 1;
  min-width: 0;
  font-family: var(--font-body);
  font-size: 13px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.alerts-banner__row-type {
  font-weight: 500;
}

.alerts-banner__row-type--critical { color: var(--accent-red); }
.alerts-banner__row-type--warning { color: var(--accent-amber); }
.alerts-banner__row-type--info { color: var(--accent-blue); }

.alerts-banner__row-sep {
  color: var(--text-muted);
  margin: 0 0.3rem;
}

.alerts-banner__row-detail {
  color: var(--alert-navy-text);
}

.alerts-banner__row-time {
  font-size: 10px;
  color: var(--text-muted);
}

.alerts-banner__row-action {
  background: none;
  border: none;
  color: var(--accent-blue);
  font-family: var(--font-body);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  flex-shrink: 0;
  padding: 0;
}

.alerts-banner__row-dismiss {
  background: none;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  flex-shrink: 0;
  padding: 0;
  font-size: 12px;
  display: flex;
}

.alerts-banner__row-dismiss:hover {
  color: var(--text-secondary);
}

.alerts-banner__more {
  width: 100%;
  background: none;
  border: none;
  padding: 0.5rem 1.25rem;
  font-family: var(--font-body);
  font-size: 12px;
  cursor: pointer;
  text-align: left;
}

.alerts-banner--critical .alerts-banner__more { color: var(--accent-red); }
.alerts-banner--warning .alerts-banner__more { color: var(--accent-amber); }
.alerts-banner--info .alerts-banner__more { color: var(--accent-blue); }
</style>